import { ChevronRightIcon } from 'lucide-react';
import { Fragment, forwardRef, type ReactNode } from 'react';
import { cn } from '../lib/utils.js';

/* Breadcrumb shadcn-style (ADR-0118/0119 §7.3). Usado no PageHeader das telas de detalhe do
   backoffice (ex.: Empresas › {nome}) para dar navegação de volta + contexto. O item corrente
   (último) é não-clicável; os anteriores recebem `href`/`onClick`. */

export interface BreadcrumbItem {
  readonly label: ReactNode;
  /** Ausente = item corrente (não navegável). */
  readonly onClick?: (() => void) | undefined;
}

export interface BreadcrumbProps {
  readonly items: readonly BreadcrumbItem[];
  readonly className?: string;
  /** Rótulo acessível da navegação (pt-BR). */
  readonly ariaLabel: string;
}

export const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items, className, ariaLabel }, ref) => (
    <nav
      ref={ref}
      aria-label={ariaLabel}
      data-slot="breadcrumb"
      className={cn('flex items-center gap-1 text-sm text-muted-foreground', className)}
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: breadcrumb curto e estável por render
          <Fragment key={i}>
            {i > 0 && (
              <ChevronRightIcon className="size-3.5 shrink-0 opacity-60" aria-hidden="true" />
            )}
            {isLast || item.onClick === undefined ? (
              <span
                className={cn('truncate', isLast && 'font-medium text-foreground')}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={item.onClick}
                className="truncate rounded transition-colors hover:text-foreground"
              >
                {item.label}
              </button>
            )}
          </Fragment>
        );
      })}
    </nav>
  ),
);
Breadcrumb.displayName = 'Breadcrumb';
